import axios from 'axios';
import { getVisitorId } from './analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Rutas públicas: NO redirigir a /login si llega un 401 desde aquí
const PUBLIC_PATHS = ['/', '/login', '/register', '/auth/callback'];

// Endpoints que pueden devolver 401 silenciosamente (carga inicial / probing)
const SILENT_401_ENDPOINTS = ['/auth/me', '/profile'];

// Create axios instance
const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true, // enviar cookie session_token (Google Auth)
});

// Add auth token + visitor id to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    // Visitor ID para atribución first-touch en /auth/register y /auth/google/session
    try {
        const visitorId = getVisitorId();
        if (visitorId) config.headers['X-Visitor-Id'] = visitorId;
    } catch {
        // ignore
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            const url = error.config?.url || '';
            const isSilent = SILENT_401_ENDPOINTS.some((ep) => url.includes(ep));
            const currentPath = window.location.pathname;
            const isPublic = PUBLIC_PATHS.includes(currentPath) || currentPath.startsWith('/auth/');

            if (!isSilent && !isPublic) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    register: (data) => api.post('/auth/register', data),
    login: (data) => api.post('/auth/login', data),
    getMe: () => api.get('/auth/me'),
    logout: () => api.post('/auth/logout'),
};

// Usage API (freemium tracking)
export const usageAPI = {
    getStatus: () => api.get('/usage/status'),
    trackNavigation: (sessionId) => api.post('/usage/track-navigation', { session_id: sessionId }),
    grantIA: (userId) => api.post(`/admin/grant-ia/${userId}`),
    revokeIA: (userId) => api.post(`/admin/revoke-ia/${userId}`),
};

// Profile API
export const profileAPI = {
    get: () => api.get('/profile'),
    update: (data) => api.put('/profile', data),
};

// Rubrics API
export const rubricsAPI = {
    getAll: (params) => api.get('/rubrics', { params }),
    get: (id) => api.get(`/rubrics/${id}`),
    create: (data) => api.post('/rubrics', data),
    update: (id, data) => api.put(`/rubrics/${id}`, data),
    delete: (id) => api.delete(`/rubrics/${id}`),
    upload: (formData, tarea, indicador) => api.post(`/rubrics/upload?tarea=${tarea}&indicador=${indicador}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// Conversations API
export const conversationsAPI = {
    getAll: (params) => api.get('/conversations', { params }),
    get: (id) => api.get(`/conversations/${id}`),
    create: (data) => api.post('/conversations', data),
    delete: (id) => api.delete(`/conversations/${id}`),
};

// Chat API
export const chatAPI = {
    send: (data) => api.post('/chat', data),
};

// Drafts API
export const draftsAPI = {
    getAll: (params) => api.get('/drafts', { params }),
    get: (id) => api.get(`/drafts/${id}`),
    create: (data) => api.post('/drafts', data),
    update: (id, data) => api.put(`/drafts/${id}`, data),
    autosave: (id, contenido) => api.patch(`/drafts/${id}/autosave`, { contenido }),
    delete: (id) => api.delete(`/drafts/${id}`),
    analyze: (id) => api.post(`/drafts/${id}/analyze`),
    improveWriting: (id, data) => api.post(`/drafts/${id}/improve-writing`, data || {}),
    applyWritingVersion: (id, data) => api.post(`/drafts/${id}/apply-writing-version`, data),
};

// Sessions API
export const sessionsAPI = {
    end: () => api.post('/sessions/end'),
};

// Account API
export const accountAPI = {
    reset: () => api.post('/account/reset', { confirm: 'RESETEAR' }),
};

// Progress API
export const progressAPI = {
    get: () => api.get('/progress'),
    update: (data) => api.put('/progress', data),
};

// Export API
export const exportAPI = {
    feedback: (draftId) => api.get(`/export/feedback/${draftId}`),
    draft: (draftId) => api.get(`/export/draft/${draftId}`),
    progress: () => api.get('/export/progress'),
};

// Dashboard API
export const dashboardAPI = {
    getStats: () => api.get('/dashboard/stats'),
};

// Radar Docente API
export const radarDocenteAPI = {
    searchEscuelas: (params) => api.get('/radar-docente/escuelas', { params }),
    getEscuela: (rbd) => api.get(`/radar-docente/escuelas/${rbd}`),
    sincronizar: (rbd) => api.post(`/radar-docente/sincronizar/${rbd}`),
    getRegiones: () => api.get('/radar-docente/regiones'),
    getComunas: (region) => api.get(`/radar-docente/comunas/${encodeURIComponent(region)}`),
};

// Admin API
export const adminAPI = {
    isAdmin: () => api.get('/auth/is-admin'),
    resetEstablecimiento: () => api.post('/admin/reset-establecimiento'),
    getStats: () => api.get('/admin/stats'),
    getUsers: () => api.get('/admin/users'),
    getUsersDaily: () => api.get('/admin/users/daily'),
    getSearches: () => api.get('/admin/searches'),
    exportUsers: () => api.get('/admin/export/users', { responseType: 'blob' }),
    // Fase 2 comercial · sólo consulta
    getPagos: (params) => api.get('/admin/pagos', { params }),
    getPagosResumen: () => api.get('/admin/pagos/resumen'),
    getPlanes: (params) => api.get('/admin/planes', { params }),
};

// Freemium API
export const freemiumAPI = {
    getStatus: () => api.get('/freemium/status'),
};

// Support API
export const supportAPI = {
    createTicket: (payload) => api.post('/support/ticket', payload),
    listTickets: (params) => api.get('/admin/support/tickets', { params }),
    updateStatus: (id, payload) => api.patch(`/admin/support/tickets/${id}`, payload),
    countOpen: () => api.get('/admin/support/count-open'),
};

export default api;
